import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Row, Col, InputNumber, Select, DatePicker, Card, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useReturn } from '../../store/returnContext';
import { useProduct } from '../../store/productContext';
import { useCustomer } from '../../store/customerContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { ReturnInvoice } from '../../types/return';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import PrintableInvoice from '../Print/PrintableInvoice';
import ItemStockHint from '../Product/ItemStockHint';

const UNITS = [
    { value: "piece", label: "Piece" },
    { value: "set", label: "Set" },
    { value: "carton", label: "Carton" },
    { value: "meter", label: "Meter" },
    { value: "roll", label: "Roll" },
    { value: "box", label: "Box" },
    { value: "other", label: "Other" },
];

interface Props {
    editReturn?: ReturnInvoice;
    onFinishEdit?: () => void;
}

// GET /returns populates items[].product with the whole product document, while
// the product Select works with plain ids — unwrap it so the row shows the name.
const getProductId = (product: any): any =>
    product && typeof product === "object"
        ? product.value ?? product.id ?? product._id
        : product;

const ReturnForm: React.FC<Props> = ({ editReturn, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addReturn, updateReturn } = useReturn();
    const { products } = useProduct();
    const { customers, fetchCustomers } = useCustomer();
    const { currencies } = useCurrency();
    const [newInvoice, setNewInvoice] = useState<ReturnInvoice | null>(null);
    const [currencyDisabled, setCurrencyDisabled] = useState(false);
    const printRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
    const currencyList = currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    }));

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: t('return_invoice') as string,
    });
    const getCustomerName = (customer?: any, customerName?: string, customersList = customers): string => {
        if (customer && typeof customer === "object") return customer.name || customerName || "-";
        if (customer) {
            const found = customersList.find(c => c.id === customer);
            return found?.name || customerName || "-";
        }
        return customerName || "-";
    };

    const getProductName = (product?: any, productList = products): string => {
        if (!product) return "-";
        if (typeof product === "object") return product.name || "-";
        const found = productList.find(p => p.id === product);
        return found?.name || "-";
    };

    const handleNewInvoice = () => {
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(),
            items: [{}],
            paid: 0,
            remain: 0,
            total: 0,
            currency: DEFAULT_CURRENCY,
        });
        setCurrencyDisabled(false);
        setNewInvoice(null)
    };

    useEffect(() => {
        fetchCustomers?.();
        if (!editReturn) {
            form.setFieldsValue({
                date: dayjs(),
                items: [{}],
                total: 0,
                currency: DEFAULT_CURRENCY,
            });
        } else {
            form.setFieldsValue({
                ...editReturn,
                items: (editReturn.items || []).map((item: any) => ({
                    ...item,
                    product: getProductId(item.product),
                })),
                date: editReturn.date ? dayjs(editReturn.date) : dayjs(),
            });
        }
    }, [editReturn]);

    const customerOptions = customers.map(c => ({
        value: c.id,
        label: `${c.name} (${c.phone || ""})`
    }));

    const productOptions = products.map(p => ({
        value: p.id,
        label: p.name,
    }));

    const handleItemChange = (_changed: any, all: any) => {
        const items = Array.isArray(all.items) ? all.items : [];
        const total = items.reduce((sum: number, item: any) => {
            if (item && item.qty && item.sellPrice) return sum + (Number(item.qty) * Number(item.sellPrice));
            return sum;
        }, 0);
        if (form.getFieldValue('total') !== total) {
            form.setFieldsValue({ total });
        }
    };

    const onFinish = async (values: any) => {
        if (!values.items || values.items.length === 0 || values.items.some((item: any) => !item.product || !item.qty || !item.sellPrice)) {
            message.error(t('return_form_at_least_one_item') as string);
            return;
        }

        // Format items for backend
        const items = values.items.map((item: any) => ({
            product: getProductId(item.product),
            unit: item.unit,
            qty: Number(item.qty),
            unitLength: item.unitLength ? Number(item.unitLength) : undefined,
            sellPrice: Number(item.sellPrice),
            attributes: item.attributes,
            note: item.note
        }));

        let payload: any = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
            items,
        };

        // Send either customer or customerName/customerPhone
        if (payload.customer) {
            delete payload.customerName;
            delete payload.customerPhone;
        } else {
            delete payload.customer;
        }

        if (editReturn) {
            updateReturn({ ...editReturn, ...payload });
            onFinishEdit?.();
            form.resetFields();
        } else {
            const createdReturn = await addReturn(payload);
            setNewInvoice(createdReturn);
            message.success(t('sale_created') as string);
            setTimeout(() => {
                handlePrint();
            }, 500);
            form.setFieldsValue({ items: [{}], currency: DEFAULT_CURRENCY });
        }
    };

    return (
        <div>
            <Row justify="end" style={{ marginBottom: 8 }}>
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleNewInvoice}
                >
                    {t('new_invoice')}
                </Button>
            </Row>
            <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{
                maxWidth: 1400,
                minWidth: 480,
                margin: '0 auto',
                padding: 16,
                background: "#fff",
                borderRadius: 18,
                boxShadow: '0 2px 16px rgba(39,21,107,0.06)'
            }}
            onValuesChange={handleItemChange}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item name="customer" label={t("return_form_customer")}>
                        <Select
                            options={customerOptions}
                            placeholder={t("return_form_select_customer") as string}
                            showSearch
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item name="customerName" label={t("return_form_customer_name")}>
                        <Input placeholder={t("return_form_customer_name_ph") as string} />
                    </Form.Item>
                    <Form.Item name="customerPhone" label={t("return_form_customer_phone")}>
                        <Input placeholder={t("return_form_customer_phone_ph") as string} />
                    </Form.Item>
                    <Form.Item name="invoiceNumber" label={t("return_form_invoice_number")} rules={[{ required: true, message: t("return_form_invoice_number_required") as string }]}>
                        <Input placeholder={t("return_form_invoice_number_ph") as string} />
                    </Form.Item>
                    <Form.Item name="date" label={t("return_form_date")} rules={[{ required: true, message: t("return_form_date_required") as string }]}>
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="total" label={t("return_form_total")} rules={[{ required: true, message: t("return_form_total_required") as string }]}>
                        <InputNumber min={0} style={{ width: "100%" }} readOnly />
                    </Form.Item>
                    <Form.Item name="currency" label={t("return_form_currency")} rules={[{ required: true, message: t("return_form_currency_required") as string }]}>
                        <Select
                            options={currencyList}
                            disabled={currencyDisabled}
                            placeholder={t("return_form_currency_ph") as string}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item name="note" label={t("return_form_note")}>
                        <Input.TextArea placeholder={t("return_form_note_ph") as string} autoSize />
                    </Form.Item>
                </Col>
            </Row>
            <Card title={t("return_form_return_items")} style={{ marginBottom: 16, borderRadius: 12 }}>
                <Form.List
                    name="items"
                    rules={[
                        {
                            validator: async (_, items) => {
                                if (!items || items.length < 1 || items.every((item: any) => !item.product)) {
                                    return Promise.reject(new Error(t("return_form_at_least_one_return_item") as string));
                                }
                            }
                        }
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            <Row gutter={8} style={{ fontWeight: 600, marginBottom: 4 }}>
                                <Col span={6}>{t("return_form_product")}</Col>
                                <Col span={3}>{t("return_form_unit")}</Col>
                                <Col span={3}>{t("return_form_qty")}</Col>
                                <Col span={4}>{t("return_form_sell_price")}</Col>
                                <Col span={4}>{t("return_form_length")}</Col>
                                <Col span={2}></Col>
                            </Row>
                            {fields.map(({ key, name, ...restField }) => {
                                const unit = form.getFieldValue(['items', name, 'unit']);
                                const showUnitLength = unit === "roll" || unit === "set" || unit === "carton" || unit === "box"
                                return (
                                    <Row key={key} gutter={8} style={{ marginBottom: 6 }}>
                                        <Col span={6}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'product']}
                                                rules={[{ required: true, message: t("return_form_select_product") as string }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    options={productOptions}
                                                    placeholder={t("return_form_product_ph") as string}
                                                    showSearch
                                                    optionFilterProp="label"
                                                />
                                            </Form.Item>
                                            <ItemStockHint form={form} name={name} products={products} type="return" />
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'unit']}
                                                rules={[{ required: true, message: t("return_form_select_unit") as string }]}
                                                style={{ marginBottom: 0 }}
                                                initialValue={UNITS[0].value}
                                            >
                                                <Select
                                                    options={UNITS}
                                                    placeholder={t("return_form_unit_ph") as string}
                                                    onChange={() => form.validateFields()} // rerender for dynamic field
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'qty']}
                                                rules={[{ required: true, message: t("return_form_enter_qty") as string }]}
                                                style={{ marginBottom: 0 }}
                                                initialValue={1}
                                            >
                                                <InputNumber min={1} placeholder={t("return_form_qty_ph") as string} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'sellPrice']}
                                                rules={[{ required: true, message: t("return_form_enter_sell_price") as string }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber min={0}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            add();
                                                        }
                                                    }}
                                                    placeholder={t("return_form_sell_price_ph") as string} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            {showUnitLength && (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'unitLength']}
                                                    rules={[{ required: true, message: t("return_form_enter_length") as string }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber min={0} placeholder={t("return_form_length_ph") as string} style={{ width: "100%" }} />
                                                </Form.Item>
                                            )}
                                        </Col>
                                        <Col span={2} style={{ display: "flex", alignItems: "center" }}>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<MinusCircleOutlined />}
                                                onClick={() => remove(name)}
                                                tabIndex={-1}
                                            />
                                        </Col>
                                    </Row>
                                );
                            })}
                            <Form.ErrorList errors={errors} />
                            <Button
                                type="dashed"
                                block
                                icon={<PlusOutlined />}
                                onClick={() => add()}
                                style={{ marginTop: 4 }}
                            >
                                {t("return_form_add_item")}
                            </Button>
                        </>
                    )}
                </Form.List>
            </Card>
            <Form.Item>
                <div style={{ display: "none" }}>
                    {newInvoice && (
                        <PrintableInvoice
                            ref={printRef}
                            type="return"
                            invoice={newInvoice}
                                getCustomerOrSupplierName={() => getCustomerName(newInvoice.customer, newInvoice.customerName, customers)}
                            getProductName={(id) => getProductName(id, products)}
                        />
                    )}
                </div>
                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                >
                    {editReturn ? t("return_form_update_return") : t("return_form_create_return")}
                </Button>
            </Form.Item>
            </Form>
        </div>
    );
};

export default ReturnForm;
